// Central image registry — drop the client's real vehicle photo paths here.
// Each slot renders an elegant placeholder frame until a photo is supplied.
export const IMAGES = {
  hero: '/images/DSC_6227.webp',
  escalade: '/images/DSC_6239.webp',
  m4: '/images/m4-5.webp',
  bmw430: '/images/b430-4.webp',
  experience1: '/images/DSC_6212.webp',
  experience2: '/images/DSC_6205.webp',
  experience3: '/images/DSC_6240.webp',
  experience4: '/images/m4-1.webp',
};

const ESCALADE_GALLERY = [
  '/images/DSC_6239.webp',
  '/images/DSC_6227.webp',
  '/images/DSC_6212.webp',
  '/images/DSC_6205.webp',
  '/images/DSC_6240.webp',
];

const M4_GALLERY = [
  '/images/m4-5.webp',
  '/images/m4-1.webp',
  '/images/m4-4.webp',
  '/images/m4-2.webp',
  '/images/m4-3.webp',
];

const B430_GALLERY = [
  '/images/b430-4.webp',
  '/images/b430-5.webp',
  '/images/b430-1.webp',
  '/images/b430-2.webp',
  '/images/b430-3.webp',
];

export const PHONE = '786-920-2715';
export const PHONE_HREF = 'tel:+17869202715';
export const SMS_HREF = 'sms:+17869202715';
export const EMAIL = 'alasvelocity@gmail.com';
export const INSTAGRAM = '@alas.velocity';
export const INSTAGRAM_HREF = 'https://instagram.com/alas.velocity';
export const DIRECTIONS_HREF = 'https://www.google.com/maps/search/?api=1&query=Miami,+FL';

export const NAV_LINKS = [
  { label: 'Fleet', href: '#fleet' },
  { label: 'Partner With Us', href: '#partner' },
  { label: 'Services', href: '#services' },
  { label: 'Why Choose Us', href: '#why-us' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Reserve', href: '#reserve' },
  { label: 'Contact', href: '#contact' },
];

export const VEHICLES = [
  {
    id: 'escalade',
    name: '2024 Cadillac Escalade ESV',
    category: 'Luxury SUV',
    price: 500,
    miles: '250 miles/day',
    image: IMAGES.escalade,
    gallery: ESCALADE_GALLERY,
    description:
      'Commanding presence and first-class comfort. The Escalade ESV is Miami\'s definitive luxury SUV — spacious, refined, and unmistakable.',
    specs: ['Seats 7', 'Apple CarPlay', 'Premium Audio', 'Leather Interior', 'Airport Delivery'],
  },
  {
    id: 'm4',
    name: '2023 BMW M4 Convertible',
    category: 'Performance Convertible',
    price: 400,
    miles: '100 miles/day',
    image: IMAGES.m4,
    gallery: M4_GALLERY,
    description:
      'M-tuned performance with the top down. A precision instrument built for Ocean Drive nights and open-air thrills.',
    specs: ['Leather', 'Apple CarPlay', 'Premium Audio'],
  },
  {
    id: 'bmw430',
    name: '2024 BMW 430i Convertible',
    category: 'Luxury Convertible',
    price: 200,
    miles: '200 miles/day',
    image: IMAGES.bmw430,
    gallery: B430_GALLERY,
    description:
      'Effortless open-air elegance. The 430i pairs refined luxury with everyday drivability — the perfect Miami companion.',
    specs: ['Leather', 'Apple CarPlay', 'Premium Audio'],
  },
];

export const SERVICES = [
  { icon: 'Plane', title: 'Airport Delivery', text: 'Your vehicle, waiting curbside at MIA or FLL the moment you land.' },
  { icon: 'Building2', title: 'Hotel Delivery', text: 'White-glove delivery to any hotel, resort, or private residence.' },
  { icon: 'Briefcase', title: 'Corporate Travel', text: 'Discreet, punctual vehicles for executives and business travel.' },
  { icon: 'Sunset', title: 'Weekend Rentals', text: 'Make the weekend unforgettable with a Friday-to-Monday escape.' },
  { icon: 'Palmtree', title: 'Vacation Rentals', text: 'Multi-day luxury for your entire South Florida stay.' },
  { icon: 'Gem', title: 'Wedding Transportation', text: 'Arrive in style on the day that deserves nothing less.' },
  { icon: 'Camera', title: 'Content Creation Rentals', text: 'Photoshoots, music videos, and productions — camera-ready fleet.', href: '#photoshoot' },
  { icon: 'Sparkles', title: 'Special Events', text: 'Galas, birthdays, anniversaries — elevate every occasion.' },
];

export const WHY_US = [
  { icon: 'Sparkles', title: 'Professionally Detailed Vehicles', text: 'Every vehicle is hand-detailed and inspected before each rental.' },
  { icon: 'Plane', title: 'Airport & Hotel Delivery', text: 'We bring the car to you — anywhere in South Florida.' },
  { icon: 'Zap', title: 'Fast Reservations', text: 'Reserve in minutes. Confirmations handled personally and promptly.' },
  { icon: 'Car', title: 'Luxury Fleet', text: 'A curated collection of late-model luxury and performance vehicles.' },
  { icon: 'Crown', title: 'Concierge Customer Service', text: 'One point of contact for everything — before, during, and after.' },
  { icon: 'CalendarRange', title: 'Flexible Rentals', text: 'Daily, weekend, weekly, or long-term — tailored to your plans.' },
  { icon: 'MapPin', title: 'South Florida Coverage', text: 'Miami, Miami Beach, Brickell, Coral Gables, Fort Lauderdale and beyond.' },
  { icon: 'Headset', title: 'Responsive Support', text: 'Available 7 days a week, 8 AM to 10 PM, by call or text.' },
];

export const EXPERIENCE = [
  {
    chapter: '01',
    title: 'Luxury, Without Compromise',
    text: 'Every vehicle in our fleet is selected, detailed, and prepared to a standard — not a schedule. Step into an interior that feels untouched, because to us, every rental is the first.',
    image: IMAGES.experience1,
  },
  {
    chapter: '02',
    title: 'Professionalism You Can Feel',
    text: 'From your first message to the final handoff, you deal with people who treat your time as the luxury it is. Clear communication, punctual delivery, immaculate presentation.',
    image: IMAGES.experience2,
  },
  {
    chapter: '03',
    title: 'Convenience, Concierge-Level',
    text: 'Airport curbside, hotel valet, your front door — the car comes to you, fueled and ready. Reservations take minutes, and plans can flex when yours do.',
    image: IMAGES.experience3,
  },
  {
    chapter: '04',
    title: 'Privacy, Respected',
    text: 'Discretion is part of the service. Your itinerary, your business. We simply make sure the drive is beyond ordinary.',
    image: IMAGES.experience4,
  },
];

export const TESTIMONIALS = [
  {
    quote: 'The Escalade was spotless — genuinely looked brand new. Communication was professional from the first text to drop-off.',
    name: 'Marcus T.',
    detail: 'Escalade ESV · Miami',
  },
  {
    quote: 'Landed at MIA and the car was already waiting. Airport delivery was seamless — easiest rental experience I\'ve ever had.',
    name: 'Danielle R.',
    detail: 'Airport Delivery · MIA',
  },
  {
    quote: 'The M4 for the weekend was pure joy. Five-star service, zero hassle, and the car was an absolute head-turner.',
    name: 'James K.',
    detail: 'BMW M4 · Miami Beach',
  },
  {
    quote: 'Booked the 430i for our anniversary. Immaculate car, easy pickup, and they even accommodated a late return gracefully.',
    name: 'Sofia & Andre',
    detail: 'BMW 430i · Coral Gables',
  },
  {
    quote: 'You can tell these are car people. Every question answered fast, every detail handled. This is how rentals should feel.',
    name: 'Robert L.',
    detail: 'Corporate Rental · Brickell',
  },
  {
    quote: 'From reservation to return, everything felt first-class. I won\'t rent from anyone else in Miami.',
    name: 'Vanessa M.',
    detail: 'Vacation Rental · Fort Lauderdale',
  },
];

export const FAQS = [
  {
    q: 'How old do I need to be?',
    a: 'Renters must be at least 21 years of age with a valid driver\'s license. Certain vehicles may have a minimum age of 25 — ask us when reserving and we\'ll confirm for your vehicle.',
  },
  {
    q: 'Do you deliver vehicles?',
    a: 'Yes. We offer delivery to airports, hotels, residences, and offices across South Florida — including MIA and FLL. Your vehicle arrives detailed, fueled, and ready to drive.',
  },
  {
    q: 'Can I rent long-term?',
    a: 'Absolutely. Weekly and monthly arrangements are available at preferred rates. Contact us with your dates and we\'ll build a plan around your stay.',
  },
  {
    q: 'Do I need insurance?',
    a: 'Yes, renters are required to carry valid auto insurance that extends to rental vehicles. Bring your proof of insurance at pickup or delivery and we\'ll verify it in minutes.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept major credit and debit cards, Zelle, and Cash App. A security deposit may be required depending on the vehicle.',
  },
  {
    q: 'How does airport delivery work?',
    a: 'Share your flight details when you reserve. We track your arrival, stage the vehicle at the terminal curbside or designated pickup area, and hand you the keys within minutes of landing.',
  },
  {
    q: 'How many miles are included?',
    a: 'Mileage varies by vehicle — the Escalade ESV includes 250 miles/day, the 430i includes 200 miles/day, and the M4 includes 100 miles/day. Additional miles are available at a per-mile rate.',
  },
  {
    q: 'What happens if I return the vehicle late?',
    a: 'We understand plans change — just call or text us as soon as you know. Short grace periods are often fine with notice; uncommunicated late returns may incur additional charges.',
  },
];

export const SERVICE_AREAS = ['Miami', 'Miami Beach', 'Brickell', 'Coral Gables', 'Fort Lauderdale', 'South Florida'];

export const HERO_STATS = [
  { icon: 'Plane', label: 'Airport Delivery' },
  { icon: 'Crown', label: 'Concierge Service' },
  { icon: 'Car', label: 'Luxury Fleet' },
  { icon: 'CalendarCheck', label: 'Available 7 Days' },
];
